import React, { useState, useEffect } from 'react';
import { accessAPI, casesAPI, calendarAPI, formsAPI, membershipAPI } from './api';

export const UserDashboard = ({ user, setCurrentView }) => {
  const [dashboardData, setDashboardData] = useState({
    cases: [],
    upcomingEvents: [],
    forms: [],
    stats: {}
  });
  const [membershipStatus, setMembershipStatus] = useState(null);
  const [membershipError, setMembershipError] = useState('');
  const [portalLoading, setPortalLoading] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const membershipRequest = accessAPI?.getState
        ? Promise.resolve(accessAPI.getState()).then((accessResponse) => (
          accessResponse?.data?.selection?.path === 'membership' && membershipAPI?.getStatus
            ? membershipAPI.getStatus()
            : { data: null }
        ))
        : Promise.resolve({ data: null });
      const [casesRes, eventsRes, formsRes, membershipRes] = await Promise.all([
        casesAPI.getAll(),
        calendarAPI.getEvents({ startAt: new Date().toISOString(), limit: 5 }),
        formsAPI.getAll(),
        Promise.resolve(membershipRequest).catch(() => ({ data: null }))
      ]);

      const upcomingEvents = (eventsRes.data.events || []).slice(0, 5);

      const incompleteForms = formsRes.data.filter(form => form.status !== 'completed');

      setDashboardData({
        cases: casesRes.data,
        upcomingEvents,
        forms: incompleteForms,
        stats: {
          totalCases: casesRes.data.length,
          activeForms: incompleteForms.length,
          upcomingEvents: upcomingEvents.length
        }
      });
      setMembershipStatus(membershipRes?.data || null);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const openBillingPortal = async () => {
    setPortalLoading(true);
    setMembershipError('');
    try {
      const response = await membershipAPI.createPortal();
      window.location.assign(response.data.portal_url);
    } catch (error) {
      setMembershipError(error.response?.data?.detail || 'Billing management is temporarily unavailable.');
    } finally {
      setPortalLoading(false);
    }
  };

  const getEventTypeIcon = (type) => {
    switch(type) {
      case 'court_date': return '⚖️';
      case 'user_confirmed_deadline': return '⏰';
      case 'filing': return '📄';
      case 'meeting': return '🤝';
      default: return '📅';
    }
  };

  const formatEventDate = (event) => new Intl.DateTimeFormat('en-CA', {
    dateStyle: 'medium',
    ...(event.all_day ? {} : { timeStyle: 'short' }),
    timeZone: event.timezone
  }).format(new Date(event.start_at));

  if (loading) {
    return (
      <div className="py-16 bg-gray-50 min-h-screen">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-blue-900 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading your dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-16 bg-gray-50 min-h-screen">
      <div className="container mx-auto px-4">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2">
            Welcome back, {user.first_name}! 👋
          </h1>
          <p className="text-lg text-gray-600">
            Here's what's happening with your family law matters.
          </p>
        </div>

        {membershipStatus?.membership_selected && (
          <section className="mb-8 rounded-lg border border-blue-200 bg-white p-6 shadow-lg" aria-labelledby="membership-heading">
            <h2 id="membership-heading" className="text-xl font-bold text-gray-800">Membership</h2>
            <p className="mt-2 text-gray-700">
              {membershipStatus.entitlement_active
                ? 'Your full-platform Membership access is active.'
                : 'Your Membership is not currently providing full-platform access.'}
            </p>
            {membershipStatus.current_period_end && (
              <p className="mt-1 text-sm text-gray-600">
                Verified paid-through date: {new Date(membershipStatus.current_period_end).toLocaleDateString()}
              </p>
            )}
            {membershipStatus.cancel_at_period_end && (
              <p className="mt-2 text-sm text-amber-800">
                Cancellation is scheduled. Access remains active through the verified paid period.
              </p>
            )}
            {membershipStatus.payment_attention_required && (
              <p className="mt-2 text-sm text-amber-800">Stripe reports that this payment needs attention.</p>
            )}
            {membershipError && <p role="alert" className="mt-2 text-sm text-red-700">{membershipError}</p>}
            {membershipStatus.billing_portal_available && (
              <button
                type="button"
                onClick={openBillingPortal}
                disabled={portalLoading}
                className="mt-4 rounded bg-blue-900 px-4 py-2 text-white disabled:bg-gray-400"
              >
                {portalLoading ? 'Opening…' : 'Manage billing with Stripe'}
              </button>
            )}
          </section>
        )}

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-center">
              <div className="bg-blue-100 p-3 rounded-full">
                <span className="text-2xl">📁</span>
              </div>
              <div className="ml-4">
                <p className="text-2xl font-bold text-gray-800">{dashboardData.stats.totalCases}</p>
                <p className="text-gray-600">Active Cases</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-center">
              <div className="bg-orange-100 p-3 rounded-full">
                <span className="text-2xl">📄</span>
              </div>
              <div className="ml-4">
                <p className="text-2xl font-bold text-gray-800">{dashboardData.stats.activeForms}</p>
                <p className="text-gray-600">Forms in Progress</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-center">
              <div className="bg-blue-100 p-3 rounded-full">
                <span className="text-2xl">📅</span>
              </div>
              <div className="ml-4">
                <p className="text-2xl font-bold text-gray-800">{dashboardData.stats.upcomingEvents}</p>
                <p className="text-gray-600">Upcoming Events</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-center">
              <div className="bg-green-100 p-3 rounded-full">
                <span className="text-2xl">🔔</span>
              </div>
              <div className="ml-4">
                <p className="text-lg font-bold text-gray-800">Not live</p>
                <p className="text-gray-600">Email Delivery</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Quick Actions */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4">Quick Actions</h2>
              <div className="space-y-3">
                <button 
                  onClick={() => setCurrentView('forms')}
                  className="w-full flex items-center space-x-3 p-3 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors"
                >
                  <span className="text-2xl">📄</span>
                  <div className="text-left">
                    <p className="font-medium text-gray-800">Complete Forms</p>
                    <p className="text-sm text-gray-600">Fill out court documents</p>
                  </div>
                </button>

                <button 
                  onClick={() => setCurrentView('calendar')}
                  className="w-full flex items-center space-x-3 p-3 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors"
                >
                  <span className="text-2xl">📅</span>
                  <div className="text-left">
                    <p className="font-medium text-gray-800">Add Event</p>
                    <p className="text-sm text-gray-600">Record dates you have confirmed</p>
                  </div>
                </button>

                <button 
                  onClick={() => setCurrentView('resources')}
                  className="w-full flex items-center space-x-3 p-3 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors"
                >
                  <span className="text-2xl">📚</span>
                  <div className="text-left">
                    <p className="font-medium text-gray-800">Browse Resources</p>
                    <p className="text-sm text-gray-600">Legal guides and videos</p>
                  </div>
                </button>
              </div>
            </div>

            {/* Calendar reminder status */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-3">Calendar reminders</h2>
              <p className="text-sm text-gray-600 mb-4">
                Real email delivery is not configured. Local reminder processing is simulated.
              </p>
              <button onClick={() => setCurrentView('calendar')} className="w-full text-blue-700 hover:text-blue-900 text-sm font-medium">
                Open calendar
              </button>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Upcoming Events */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4">Upcoming calendar events</h2>
              {dashboardData.upcomingEvents.length > 0 ? (
                <div className="space-y-4">
                  {dashboardData.upcomingEvents.map((event) => (
                    <div key={event.event_id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-4">
                        <span className="text-2xl">{getEventTypeIcon(event.event_type)}</span>
                        <div>
                          <h3 className="font-medium text-gray-800">{event.title}</h3>
                          <p className="text-sm text-gray-600">
                            {formatEventDate(event)} ({event.timezone})
                          </p>
                          {event.location && (
                            <p className="text-sm text-gray-500">📍 {event.location}</p>
                          )}
                        </div>
                      </div>
                      <span className="text-xs font-medium text-gray-600 bg-white px-2 py-1 rounded">
                        User-entered
                      </span>
                    </div>
                  ))}
                  <button 
                    onClick={() => setCurrentView('calendar')}
                    className="w-full text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    View Full Calendar
                  </button>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-600 mb-4">No upcoming events scheduled.</p>
                  <button 
                    onClick={() => setCurrentView('calendar')}
                    className="bg-blue-900 text-white px-4 py-2 rounded hover:bg-blue-800 transition-colors"
                  >
                    Add Your First Event
                  </button>
                </div>
              )}
            </div>

            {/* Active Cases */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4">Your Cases</h2>
              {dashboardData.cases.length > 0 ? (
                <div className="space-y-4">
                  {dashboardData.cases.map((case_item) => (
                    <div key={case_item.id} className="p-4 border border-gray-200 rounded-lg hover:border-orange-500 transition-colors">
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="font-medium text-gray-800">{case_item.title}</h3>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          case_item.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {case_item.status.toUpperCase()}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">Type: {case_item.case_type}</p>
                      {case_item.court_location && (
                        <p className="text-sm text-gray-500">📍 {case_item.court_location}</p>
                      )}
                      <p className="text-xs text-gray-500 mt-2">
                        Created: {new Date(case_item.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-600 mb-4">You haven't created any cases yet.</p>
                  <button className="bg-orange-500 text-white px-4 py-2 rounded hover:bg-orange-600 transition-colors">
                    Create Your First Case
                  </button>
                </div>
              )}
            </div>

            {/* Forms in Progress */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4">Forms in Progress</h2>
              {dashboardData.forms.length > 0 ? (
                <div className="space-y-4">
                  {dashboardData.forms.map((form) => (
                    <div key={form.id} className="p-4 border border-gray-200 rounded-lg">
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="font-medium text-gray-800">{form.form_name}</h3>
                        <span className="text-sm text-gray-600">{Math.round(form.completion_percentage)}% complete</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                          style={{ width: `${form.completion_percentage}%` }}
                        ></div>
                      </div>
                      <p className="text-xs text-gray-500">
                        Last updated: {new Date(form.updated_at).toLocaleDateString()}
                      </p>
                    </div>
                  ))}
                  <button 
                    onClick={() => setCurrentView('forms')}
                    className="w-full text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    View All Forms
                  </button>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-600 mb-4">No forms started yet.</p>
                  <button 
                    onClick={() => setCurrentView('forms')}
                    className="bg-blue-900 text-white px-4 py-2 rounded hover:bg-blue-800 transition-colors"
                  >
                    Browse Available Forms
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Emergency Resources */}
        <div className="mt-8 bg-red-50 border border-red-200 rounded-lg p-6">
          <h2 className="text-xl font-bold text-red-800 mb-4">🚨 Emergency Resources</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center">
              <h3 className="font-semibold text-red-700">Crisis Support</h3>
              <p className="text-2xl font-bold text-red-800">1-866-863-0511</p>
              <p className="text-sm text-red-600">24/7 Assaulted Women's Helpline</p>
            </div>
            <div className="text-center">
              <h3 className="font-semibold text-red-700">Emergency</h3>
              <p className="text-2xl font-bold text-red-800">911</p>
              <p className="text-sm text-red-600">Police Emergency</p>
            </div>
            <div className="text-center">
              <h3 className="font-semibold text-red-700">Kids Help Phone</h3>
              <p className="text-2xl font-bold text-red-800">1-800-668-6868</p>
              <p className="text-sm text-red-600">24/7 Support for Children</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
